/* eslint-disable sonarjs/cognitive-complexity */
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  RACButton,
  RACTextbox,
  RACDateRangePicker,
} from '@rentacenter/racstrap';
import { customerStyles } from '../customerStyles';
import { ReactComponent as SearchImageDefault } from '../../assets/images/search-btn.svg';
import { ReactComponent as SearchIcon } from '../../assets/images/Search-icon-Grey.svg';
import { ReactComponent as Filter } from '../../assets/images/Filter.svg';
import { ReactComponent as UpArrow } from '../../assets/images/up-arrow-filter.svg';
import { ReactComponent as DownArrow } from '../../assets/images/down-arrow-filter.svg';
import { Breadcrumb } from './Breadcrumb';
import { SearchTextboxWrapper } from './searchTextBoxWrapper';
import { SearchableDropdown } from './SearchableDropdown';
import { useReferenceContext } from '../../context/CustomerService/ReferenceProvider';
import { toOptions } from './utils';
import { hasAnyCriteria, SearchState } from '../../utils/utils';
import { SearchBoxHandle } from '../../types/types';
import { CustomDropdown } from './CustomDropdown';

interface SearchBoxProps {
  search: SearchState;
  pageType?: string;
  hasAccess?: boolean;
  onSearchChange: (name: string, value: string | string[]) => void;
  onSubmit: (criteria: SearchState) => void;
  onClearClick: () => void;
  onFilterModeChange?: (open: boolean) => void;
}

const SearchBox = forwardRef<SearchBoxHandle, SearchBoxProps>(
  (
    {
      search,
      pageType,
      hasAccess = false,
      onSearchChange,
      onSubmit,
      onClearClick,
      onFilterModeChange,
    },
    ref
  ) => {
    const classes = customerStyles();
    const filteredActionOptions = useMemo(
      () => [
        { label: 'None', value: '0' },
        { label: 'ESB', value: 'ESB' },
      ],
      []
    );

    const [selectedFilter, setSelectedFilter] = useState('');
    const [displaySearch, setDisplaySearch] = useState<boolean>(false);
    const [dueDateFrom, setDueDateFrom] = useState<string>('');
    const [dueDateTo, setDueDateTo] = useState<string>('');
    const { references, loading } = useReferenceContext();

    const handleSimpleFilterChange = (value: string) => {
      setSelectedFilter(value);
      const openedBy = value === '0' ? '' : value;
      onSearchChange('openedBy', openedBy);
    };

    useImperativeHandle(
      ref,
      () => ({
        isFilterPanelOpen: () => displaySearch,
        openFilterPanel: () => setDisplaySearch(true),
        closeFilterPanel: () => setDisplaySearch(false),
      }),
      [displaySearch]
    );

    const formatDate = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const todayStr = formatDate(new Date());

    const addDays = (dateStr: string, days: number) => {
      const d = new Date(dateStr);
      d.setDate(d.getDate() + days);
      return formatDate(d);
    };

    const addMonths = (dateStr: string, months: number) => {
      const d = new Date(dateStr);
      d.setMonth(d.getMonth() + months);
      return formatDate(d);
    };

    const minDateStr = (a: string, b: string) =>
      new Date(a).getTime() < new Date(b).getTime() ? a : b;

    const computedMinTo = dueDateFrom ? addDays(dueDateFrom, 1) : undefined;
    const computedMaxTo = dueDateFrom
      ? minDateStr(addMonths(dueDateFrom, 6), todayStr)
      : todayStr;

    const isSearchEnabled = () => {
      if (displaySearch) {
        return hasAnyCriteria(search);
      }
      const hasSearchVal = (search.searchValue ?? '').trim().length >= 2;
      const hasFilterVal = (selectedFilter ?? '').trim().length > 0;
      return hasSearchVal || hasFilterVal;
    };

    const isClearEnabled = () => {
      if (displaySearch) {
        return hasAnyCriteria(search);
      }
      const hasSearchVal = (search.searchValue ?? '').trim().length > 0;
      const hasFilterVal = (selectedFilter ?? '').trim().length > 0;
      return hasSearchVal || hasFilterVal;
    };

    const handleClear = () => {
      setSelectedFilter('');
      setDueDateFrom('');
      setDueDateTo('');
      onClearClick();
    };

    const toggleFilterPanel = () => {
      const next = !displaySearch;
      setSelectedFilter('');
      setDueDateFrom('');
      setDueDateTo('');
      onFilterModeChange?.(next);
      setDisplaySearch(next);
    };

    const buildCriteriaForSubmit = (): SearchState => {
      const from = dueDateFrom || search.advanceSearch.startDate || '';
      let to = dueDateTo || search.advanceSearch.endDate || '';

      if (displaySearch && from) {
        const maxAllowedTo = minDateStr(addMonths(from, 6), todayStr);
        const minAllowedTo = addDays(from, 1);
        to = to || maxAllowedTo;
        if (new Date(to) > new Date(maxAllowedTo)) {
          to = maxAllowedTo;
        }
        if (new Date(to) < new Date(minAllowedTo)) {
          to = minAllowedTo;
        }
        if (new Date(to) > new Date(todayStr)) {
          to = todayStr;
        }
      }

      return {
        ...search,
        advanceSearch: {
          ...search.advanceSearch,
          startDate: from,
          endDate: to,
        },
      };
    };

    const onSearch = () => {
      const criteria = buildCriteriaForSubmit();
      setDueDateFrom(criteria.advanceSearch.startDate || '');
      setDueDateTo(criteria.advanceSearch.endDate || '');
      onSubmit(criteria);
    };

    return (
      <Grid container className={`${classes.px2} ${classes.mb3}`}>
        <Grid item md={12} style={{ marginBottom: 15 }}>
          <Breadcrumb />
        </Grid>

        <Grid container className={`${classes.mb3}`}>
          <Grid item md={6}>
            <Typography
              variant="h6"
              style={{ marginBottom: 16, fontWeight: 600 }}
            >
              Search Criteria
            </Typography>
          </Grid>

          {hasAccess && (
            <Grid item md={6}>
              <RACButton
                color="primary"
                variant="contained"
                size="small"
                className={`${classes.mt9} ${classes.floatRight}`}
                onClick={toggleFilterPanel}
              >
                <Filter className={classes.mR1} />
                Filter
                {displaySearch ? <UpArrow /> : <DownArrow />}
              </RACButton>
            </Grid>
          )}
        </Grid>

        {/* ================= SIMPLE MODE ================= */}
        {!displaySearch && (
          <Grid item md={12}>
            <Card className={classes.card}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item md={7}>
                    <SearchTextboxWrapper
                      onSearchClick={onSearch}
                      searchIcon={<SearchIcon />}
                    >
                      <RACTextbox
                        name="searchValue"
                        value={search.searchValue}
                        inputlabel="Search by"
                        OnChange={(e) =>
                          onSearchChange('searchValue', e.target.value)
                        }
                        placeHolder="Incident Number, First name, Last Name, Phone Number, SN Number"
                        OnKeydown={(e) => {
                          if (e.key === 'Enter' && isSearchEnabled())
                            onSearch();
                        }}
                        className={classes.searchBoxStyle}
                      />
                    </SearchTextboxWrapper>
                  </Grid>

                  {pageType !== 'OpenByMe' && (
                    <Grid item md={2} style={{ marginTop: 8 }}>
                      <CustomDropdown
                        inputLabel="Filter By"
                        options={filteredActionOptions}
                        defaultValue={selectedFilter}
                        onChange={(e: string) => {
                          handleSimpleFilterChange(e);
                        }}
                      />
                    </Grid>
                  )}

                  <Grid
                    item
                    md={1.5}
                    style={{
                      display: 'flex',
                      marginLeft: 'auto',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      component="span"
                      className={classes.customerRaclink}
                      style={{
                        cursor: isClearEnabled() ? 'pointer' : 'default',
                        opacity: isClearEnabled() ? 1 : 0.5,
                      }}
                      onClick={() => isClearEnabled() && handleClear()}
                    >
                      Clear
                    </Typography>
                  </Grid>

                  <Grid item md={1.5} style={{ display: 'flex' }}>
                    <RACButton
                      startIcon={<SearchImageDefault />}
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={!isSearchEnabled()}
                      onClick={onSearch}
                      className={`${classes.floatRight} ${classes.ms2} ${classes.mt2}`}
                    >
                      Search
                    </RACButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* ================= ADVANCED MODE ================= */}
        {hasAccess && displaySearch && (
          <Grid item md={12}>
            <Card className={classes.card}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item md={4}>
                    <SearchTextboxWrapper
                      onSearchClick={onSearch}
                      searchIcon={<SearchIcon />}
                    >
                      <RACTextbox
                        name="searchValue"
                        value={search.searchValue}
                        inputlabel="Search Incident"
                        OnChange={(e) =>
                          onSearchChange('searchValue', e.target.value)
                        }
                        placeHolder="Incident Number, First name, Last Name, Phone Number, SN Number"
                        className={classes.searchBoxStyle}
                      />
                    </SearchTextboxWrapper>
                  </Grid>
                </Grid>

                <Grid container spacing={2} style={{ marginTop: 16 }}>
                  <Grid item md={2}>
                    <RACTextbox
                      inputlabel="Short Description"
                      value={search.advanceSearch.shortDescription}
                      maxlength={50}
                      OnChange={(e) =>
                        onSearchChange('shortDescription', e.target.value)
                      }
                      className={classes.searchBoxStyle}
                    />
                  </Grid>

                  <Grid item md={2}>
                    <RACTextbox
                      inputlabel="Description"
                      value={search.advanceSearch.description}
                      maxlength={50}
                      OnChange={(e) =>
                        onSearchChange('description', e.target.value)
                      }
                      className={classes.searchBoxStyle}
                    />
                  </Grid>

                  <Grid item md={2} style={{ marginTop: 8 }}>
                    <CustomDropdown
                      inputLabel="Channel"
                      loading={loading}
                      options={toOptions(references?.channel)}
                      defaultValue={search.advanceSearch.channels}
                      onChange={(e) => onSearchChange('channel', e)}
                    />
                  </Grid>

                  <Grid item md={2} style={{ marginTop: 8 }}>
                    <SearchableDropdown
                      options={toOptions(references?.category)}
                      selection={async (val: string) =>
                        onSearchChange('category', val ?? '')
                      }
                      defaultValue={search.advanceSearch.category}
                      inputLabel="Category"
                      loading={loading}
                      isDisabled={loading}
                      required={false}
                      showSearchIcon={false}
                    />
                  </Grid>

                  <Grid item md={2} style={{ marginTop: 8 }}>
                    <SearchableDropdown
                      options={toOptions(references?.sub_category)}
                      selection={async (val: string) =>
                        onSearchChange('subCategory', val ?? '')
                      }
                      defaultValue={search.advanceSearch.subCategory}
                      inputLabel="Subcategory"
                      loading={loading}
                      isDisabled={loading}
                      required={false}
                      showSearchIcon={false}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} style={{ marginTop: 16 }}>
                  <Grid item md={2}>
                    <SearchableDropdown
                      options={toOptions(references?.action_type)}
                      selection={async (val: string) =>
                        onSearchChange('action', val ?? '')
                      }
                      defaultValue={search.advanceSearch.actions}
                      inputLabel="Action"
                      loading={loading}
                      isDisabled={loading}
                      required={false}
                      showSearchIcon={false}
                    />
                  </Grid>

                  <Grid item md={4} style={{ marginTop: 4 }}>
                    <RACDateRangePicker
                      name="dueDate"
                      fromLabel="From"
                      toLabel="To"
                      fromValue={dueDateFrom}
                      toValue={dueDateTo}
                      maxFrom={todayStr}
                      minTo={computedMinTo}
                      maxTo={computedMaxTo}
                      onFromChanged={(value: string) => {
                        setDueDateFrom(value);

                        if (value) {
                          const newMaxTo = minDateStr(
                            addMonths(value, 6),
                            todayStr
                          );
                          const newMinTo = addDays(value, 1);

                          if (
                            dueDateTo &&
                            new Date(dueDateTo) > new Date(newMaxTo)
                          ) {
                            setDueDateTo(newMaxTo);
                          }
                          if (
                            dueDateTo &&
                            new Date(dueDateTo) < new Date(newMinTo)
                          ) {
                            setDueDateTo(newMinTo);
                          }
                        }

                        onSearchChange('dueDateFrom', value);
                      }}
                      onToChanged={(value: string) => {
                        setDueDateTo(value);
                        onSearchChange('dueDateTo', value);
                      }}
                    />
                  </Grid>

                  <Grid item md={2}>
                    <SearchableDropdown
                      options={toOptions(references?.openby_list)}
                      isMulti
                      value={search.openedBy ?? ''}
                      selection={(v: string) => onSearchChange('openedBy', v)}
                      inputLabel="Opened By"
                      loading={loading}
                      isDisabled={loading}
                      required={false}
                      showSearchIcon={false}
                    />
                  </Grid>

                  <Grid
                    item
                    md={1.5}
                    style={{
                      display: 'flex',
                      marginLeft: 'auto',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      component="span"
                      className={`${classes.customerRaclink} ${classes.mt9}`}
                      style={{
                        cursor: isClearEnabled() ? 'pointer' : 'default',
                        opacity: isClearEnabled() ? 1 : 0.5,
                      }}
                      onClick={() => isClearEnabled() && handleClear()}
                    >
                      Clear
                    </Typography>
                  </Grid>

                  <Grid item md={1.5} style={{ display: 'flex' }}>
                    <RACButton
                      startIcon={<SearchImageDefault />}
                      variant="contained"
                      color="primary"
                      size="small"
                      className={`${classes.floatRight} ${classes.ms2} ${classes.mt2}`}
                      disabled={!isSearchEnabled()}
                      onClick={onSearch}
                    >
                      Search
                    </RACButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  }
);

SearchBox.displayName = 'SearchBox';
export default SearchBox;
